import { useState, type ReactNode } from 'react';
import ConfirmDialog from '@/components/common/ConfirmDialog';
import { useCanvasStore } from '@/store/canvasStore';

/**
 * Ask before a logout discards unsaved model changes (#2272). Shared by every
 * logout entry point that can sit over the model builder: the navbar's
 * Logout and the re-auth prompt's "Log out".
 *
 * `guard(proceed)` runs `proceed` at once when nothing is unsaved; otherwise
 * it opens `dialog`, which the caller renders, and runs `proceed` only on
 * "Log out". Declining runs nothing, so a re-auth prompt and every request
 * parked behind it stay as they were.
 *
 * The dialog opens over the @brew/ui re-auth overlay: both overlays are
 * `.brew-modal-overlay` (same z-index) portalled to <body>, and this one is
 * mounted later, so it paints on top, and `Modal` makes every earlier body
 * child, the re-auth overlay included, inert while it is open.
 */
export function useUnsavedModelGuard(): { guard: (proceed: () => void) => void; dialog: ReactNode } {
  const [pending, setPending] = useState<(() => void) | null>(null);

  const guard = (proceed: () => void) => {
    if (!useCanvasStore.getState().modified) {
      proceed();
      return;
    }
    setPending(() => proceed);
  };

  const dialog = (
    <ConfirmDialog
      open={pending !== null}
      title="Discard unsaved changes?"
      message="This model has unsaved changes. Logging out discards them."
      confirmLabel="Log out"
      cancelLabel="Keep working"
      danger
      initialFocus="cancel"
      onConfirm={() => {
        const proceed = pending;
        setPending(null);
        proceed?.();
      }}
      onCancel={() => setPending(null)}
    />
  );

  return { guard, dialog };
}
